import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { dateUtil } from "src/app/support/date-util";
import { Observable } from "rxjs";
import { map, switchMap } from "rxjs/operators";
import { UserNotify } from "src/app/support/notify-decorator";
import { environment } from "src/environments/environment";
import {
  Appointment,
  AppointmentLight,
  AppointmentPublic
} from "../models/appointment/appointment";
import { AppointmentCreate } from "../models/appointment/appointment.create";
import { AppointmentLoad } from "../models/appointment/appointment.load";
import { AppointmentReschedule } from "../models/appointment/appointment.reschedule";
import { AppointmentSearch } from "../models/appointment/appointment.search";
import { AppointmentUpdate } from "../models/appointment/appointment.update";
import { API } from "src/app/backend/api";
import { AttachmentV2 } from "src/app/backend/models/attachment/attachment";
import { appState } from "src/app/core/state";
import { first, isUndefined, omitBy, pickBy } from "lodash-es";
import { RRule } from "rrule";
import { Event } from "src/app/support/decorators/event.decorator";
import {
  AppointmentCanceledEvent,
  AppointmentCreatedEvent,
  AppointmentDeletedEvent,
  AppointmentRescheduledEvent,
  AppointmentUpdatedEvent
} from "src/app/events/appointment-events.service";
import { HubEvent } from "src/app/core/hub/hub-events";
import { PrintService } from "src/app/services/print/print.service";
import { TemplateService } from "src/app/services/template.service";
import { getCustomerNominative } from "src/app/support/customer";
import { sntTranslate } from "src/app/support/translate";
import { LocalStorageService } from "src/app/services/local-storage.service";

@Injectable()
export class AppointmentBackend {
  private readonly http = inject(HttpClient);

  private readonly printService = inject(PrintService);
  private readonly templateService = inject(TemplateService);
  private readonly localStorageService = inject(LocalStorageService);

  load(request: AppointmentLoad): Observable<Appointment> {
    return this.http
      .get<Appointment>(API.CALENDAR.APPOINTMENTS.ID(request.id))
      .pipe(
        map(extendAppointment),
        map((appointment) => {
          if (request.pattern) {
            const startAtPattern = dateUtil(request.pattern, "YYYYMMDDHHmm");

            appointment.startAt = startAtPattern.toISOString();

            appointment.endAt = startAtPattern
              // @ts-ignore [expect-error]: 🐛 There is a null/undefined issue here which could cause bugs! Please fix me.
              .add(appointment.duration, "minute")
              .toISOString();
          }

          return appointment;
        })
      );
  }

  loadPublic(id: string): Observable<AppointmentPublic> {
    return this.http.get<AppointmentPublic>(
      API.CALENDAR.PUBLIC.APPOINTMENTS.ID(id)
    );
  }

  loadLastForPatient({
    patientId
  }: {
    patientId: string;
  }): Observable<AppointmentLight | undefined> {
    return this.http
      .get<AppointmentLight[]>(API.CALENDAR.APPOINTMENTS.LIST(), {
        params: {
          patientId,
          light: true,
          startAt: dateUtil().add(-1000, "years").toISOString(),
          endAt: dateUtil().add(1000, "years").toISOString()
        }
      })
      .pipe(
        map((appointments: AppointmentLight[]) => {
          return first(
            appointments.sort((left, right) =>
              dateUtil(left.startAt).isBefore(right.endAt) ? 1 : -1
            )
          );
        })
      );
  }

  list(
    request: AppointmentSearch,
    options?: {
      component: string;
    }
  ): Observable<Appointment[]> {
    return this.http
      .get<Appointment[]>(API.CALENDAR.APPOINTMENTS.LIST(), {
        headers: options?.component
          ? {
              component: options?.component
            }
          : undefined,
        params: pickBy(request)
      })
      .pipe(map((list) => list.map(extendAppointment)));
  }

  listAllForPatient(patientId: string): Observable<Appointment[]> {
    return this.list({
      startAt: dateUtil().add(-100, "years").toISOString(),
      endAt: dateUtil().add(100, "years").toISOString(),
      patientId: patientId
    }).pipe(
      map((appointments) =>
        appointments.sort((left, right) =>
          dateUtil(left.startAt).isBefore(right.startAt) ? 1 : -1
        )
      )
    );
  }

  listShowed(request: AppointmentSearch): Observable<Appointment[]> {
    return this.list(request).pipe(
      map((appointements) => appointements.filter((x) => !x.noShowed))
    );
  }

  listToReschedule(): Observable<Appointment[]> {
    return this.http
      .get<Appointment[]>(`${environment.snoots.api.calendar}/appointments`, {
        params: {
          startAt: dateUtil().add(-50, "days").toISOString(),
          endAt: dateUtil().add(50, "days").toISOString()
        }
      })
      .pipe(
        map((list) => {
          return list.filter((x) => !!x.canceledAt);
        }),
        map((list) => list.map(extendAppointment))
      );
  }

  listToDay(
    params?:
      | {
          staffId?: string | undefined;
          active?: boolean | undefined;
        }
      | undefined
  ): Observable<Appointment[]> {
    return this.list({
      startAt: dateUtil()
        .set("hour", 0)
        .set("minute", 0)
        .set("second", 0)
        .toISOString(),
      endAt: dateUtil()
        .set("hour", 23)
        .set("minute", 59)
        .set("second", 59)
        .toISOString(),
      staffIds: params?.staffId,
      active: params?.active
    }).pipe(
      map((appointments) =>
        appointments.sort(
          (a, b) =>
            dateUtil(a.startAt).toDate().getTime() -
            dateUtil(b.startAt).toDate().getTime()
        )
      )
    );
  }

  listToDayShowed(): Observable<Appointment[]> {
    return this.listToDay({
      staffId: undefined,
      active: true
    }).pipe(map((appointments) => appointments.filter((x) => !x.noShowed)));
  }

  listMyToDayNotCheckedInAssignedToMemberShowed(): Observable<Appointment[]> {
    return this.listToDay({
      staffId: appState.member!.id
    }).pipe(
      map((appointments) => {
        return appointments.filter((x) => !x.ticket);
      }),
      map((appointments) => appointments.filter((x) => !x.noShowed))
    );
  }

  search(request: AppointmentSearch) {
    return this.list({
      ...request,
      startAt: dateUtil()
        .add(-10000, "day")
        .set("hour", 0)
        .set("minute", 0)
        .set("second", 0)
        .toISOString(),
      endAt: dateUtil()
        .add(10000, "day")
        .set("hour", 23)
        .set("minute", 59)
        .set("second", 59)
        .toISOString()
    });
  }

  searchForCheckIn(): Observable<Appointment[]> {
    return this.list({});
  }

  loadAttachments(id: string): Observable<AttachmentV2[]> {
    return this.http
      .get<AttachmentV2[]>(
        `${environment.snoots.api.calendar}/appointments/${id}/attachments`
      )
      .pipe(
        map((attachments) => {
          return attachments.map((x) => ({
            ...x,
            extension: x.name?.endsWith(".pdf") ? "pdf" : undefined
          }));
        })
      );
  }

  loadNextForCustomer(customerId: string): Observable<Appointment | undefined> {
    return this.list({
      customerId,
      startAt: dateUtil().toISOString(),
      endAt: dateUtil().add(1000, "years").toISOString()
    }).pipe(map((appointments: Appointment[]) => first(appointments)));
  }

  listTodayScheduled({
    customerId,
    patientId
  }: {
    customerId: string;
    patientId?: string | undefined;
  }): Observable<Appointment[]> {
    return this.list({
      customerId,
      patientId,
      startAt: dateUtil().startOf("day").toISOString(),
      endAt: dateUtil().endOf("day").toISOString(),
      active: true
    }).pipe(
      map((appointments: Appointment[]) =>
        appointments
          .filter(
            (appointment: Appointment) =>
              !appointment.ticket && !appointment.noShowed
          )
          .sort(
            (left: Appointment, right: Appointment) =>
              dateUtil(left.startAt).toDate().getTime() -
              dateUtil(right.startAt).toDate().getTime()
          )
      )
    );
  }

  addAttachment(
    id: string,
    name: string,
    storageId: string,
    type: string,
    size: number
  ): Observable<void> {
    return this.http.post<void>(
      `${environment.snoots.api.calendar}/appointments/${id}/attachments`,
      {
        name,
        storageId,
        type,
        status: "uploaded",
        size
      }
    );
  }

  deleteAttachment(id: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.snoots.api.calendar}/attachments/${id}`
    );
  }

  @Event(HubEvent.APPOINTMENT_CREATED, {
    dataFactory: (
      args: any,
      appointment: Appointment
    ): AppointmentCreatedEvent => {
      return {
        id: appointment.id
      };
    }
  })
  @UserNotify({
    successMessage: marker("Appuntamento creato"),
    errorMessage: marker("Impossibile creare l'appuntamento")
  })
  create(request: AppointmentCreate): Observable<Appointment> {
    return this.http.post<Appointment>(
      `${environment.snoots.api.calendar}/appointments`,
      request
    );
  }

  @Event(HubEvent.APPOINTMENT_UPDATED, {
    dataFactory: (args: any): AppointmentUpdatedEvent => {
      return {
        id: args[0]
      };
    }
  })
  @UserNotify({
    successMessage: marker("Appuntamento aggiornato"),
    errorMessage: marker("Impossibile aggiornare l'appuntamento")
  })
  update(id: string, payload: Partial<AppointmentUpdate>): Observable<void> {
    const { tenant, ...payloadWithoutTenant } = payload;
    return this.http.patch<void>(
      `${environment.snoots.api.calendar}/appointments/${id}`,
      payloadWithoutTenant
    );
  }

  @Event(HubEvent.APPOINTMENT_RESCHEDULED, {
    dataFactory: (args: any): AppointmentRescheduledEvent => {
      return {
        id: args[0]
      };
    }
  })
  @UserNotify({
    successMessage: marker("Appuntamento aggiornato"),
    errorMessage: marker("Impossibile aggiornare l'appuntamento")
  })
  updateAndReschedule(
    id: string,
    request: Partial<AppointmentUpdate>,
    startAt: string,
    endAt: string
  ): Observable<void> {
    const { tenant, ...payloadWithoutTenant } = request;

    return this.http
      .patch<void>(
        `${environment.snoots.api.calendar}/appointments/${id}`,
        payloadWithoutTenant
      )
      .pipe(
        switchMap(() =>
          this.load({ id }).pipe(
            switchMap((appointment) => {
              const duration = dateUtil(endAt).diff(
                dateUtil(startAt),
                "minutes"
              );

              let pattern;
              let start = dateUtil(startAt);
              let until = dateUtil(endAt);

              if (appointment.pattern) {
                start = dateUtil(startAt)
                  .set("hour", dateUtil(startAt).get("hour"))
                  .set("minute", dateUtil(startAt).get("minute"));

                until = dateUtil(endAt)
                  .set("hour", dateUtil(endAt).get("hour"))
                  .set("minute", dateUtil(endAt).get("minute"));

                const options = RRule.parseString(appointment.pattern);
                options.until = until.toDate();
                options.dtstart = start.toDate();

                const rule = new RRule(options);
                pattern = rule.toString();
              }

              return this.http.put<void>(
                `${environment.snoots.api.calendar}/appointments/${id}/reschedule`,
                {
                  startAt: start.toDate(),
                  endAt: until.toDate(),
                  duration,
                  pattern
                }
              );
            })
          )
        )
      );
  }

  @Event(HubEvent.APPOINTMENT_DELETED, {
    dataFactory: (args: any): AppointmentDeletedEvent => {
      return {
        id: args[0]
      };
    }
  })
  @UserNotify({
    successMessage: marker("Appuntamento eliminato"),
    errorMessage: marker("Impossibile eliminare l'appuntamento")
  })
  delete(id: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.snoots.api.calendar}/appointments/${id}`
    );
  }

  @Event(HubEvent.APPOINTMENT_CANCELED, {
    dataFactory: (args: any): AppointmentCanceledEvent => {
      return {
        id: args[0]
      };
    }
  })
  @UserNotify({
    successMessage: marker("Appuntamento annullato"),
    errorMessage: marker("Impossibile annullare l'appuntamento")
  })
  cancel(id: string, rejectionReason?: string): Observable<void> {
    return this.http.put<void>(
      `${environment.snoots.api.calendar}/appointments/${id}/cancel`,
      {
        rejectionReason
      }
    );
  }

  @UserNotify({
    successMessage: marker("Notifica inviata"),
    errorMessage: marker("Impossibile inviare la notifica")
  })
  send(id: string, options?: { note?: string }): Observable<void> {
    return this.http.put<void>(
      `${environment.snoots.api.calendar}/appointments/${id}/send`,
      omitBy({ notes: options?.note }, isUndefined)
    );
  }

  @Event(HubEvent.APPOINTMENT_RESCHEDULED, {
    dataFactory: (args: any): AppointmentRescheduledEvent => {
      return {
        id: args[0]
      };
    }
  })
  @UserNotify({
    successMessage: marker("Appuntamento rischedulato"),
    errorMessage: marker("Impossibile rischedulare l'appuntamento")
  })
  reschedule(
    id: string,
    request: Partial<AppointmentReschedule>
  ): Observable<void> {
    return this.load({ id }).pipe(
      switchMap((appointment) => {
        const duration = dateUtil(request.endAt).diff(
          dateUtil(request.startAt),
          "minutes"
        );

        let pattern;
        let start = dateUtil(request.startAt);
        let until = dateUtil(request.endAt);

        if (appointment.pattern) {
          start = dateUtil(appointment.startAt)
            .set("hour", dateUtil(request.startAt).get("hour"))
            .set("minute", dateUtil(request.startAt).get("minute"));

          until = dateUtil(appointment.endAt)
            .set("hour", dateUtil(request.endAt).get("hour"))
            .set("minute", dateUtil(request.endAt).get("minute"));

          const options = RRule.parseString(appointment.pattern);
          options.until = until.toDate();
          options.dtstart = start.toDate();

          const rule = new RRule(options);
          pattern = rule.toString();
        }

        return this.http.put<void>(
          `${environment.snoots.api.calendar}/appointments/${id}/reschedule`,
          {
            ...request,
            startAt: start.toDate(),
            endAt: until.toDate(),
            duration,
            pattern
          }
        );
      })
    );
  }

  noShow(id: string): Observable<void> {
    return this.http.put<void>(
      `${environment.snoots.api.calendar}/appointments/${id}/no-show`,
      {}
    );
  }

  @UserNotify({
    successMessage: marker("Partecipanti notificati"),
    errorMessage: marker("Impossibile rischedulare l'appuntamento")
  })
  sendNotificationAppointmentUpdated(
    id: string,
    options?: { note?: string }
  ): Observable<void> {
    return this.http.put<void>(
      `${environment.snoots.api.calendar}/appointments/${id}/send`,
      omitBy({ notes: options?.note }, isUndefined)
    );
  }

  solicitCancelation(
    id: string,
    request: { rejectionReason: string },
    token?: string
  ): Observable<void> {
    const params: { token?: string } = {};

    if (token) {
      params["token"] = token;
    }

    return this.http.put<void>(
      `${environment.snoots.api.calendar}/public/appointments/${id}/cancel`,
      request,
      { params }
    );
  }

  @UserNotify({
    successMessage: marker("Richiesta di rischedulazione inviata"),
    errorMessage: marker("Impossibile rischedulare l'appuntamento")
  })
  solicitReschedule(
    id: string,
    request: any,
    token?: string
  ): Observable<void> {
    const { internal, duration, ...rest } = request;

    const params: { token?: string } = {};

    if (token) {
      params["token"] = token;
    }

    return this.http.put<void>(
      `${environment.snoots.api.calendar}/public/appointments/${id}/reschedule`,
      rest,
      { params }
    );
  }

  @UserNotify({
    successMessage: marker("Ritardo segnalato"),
    errorMessage: marker("Impossibile segnalare il ritardo")
  })
  solicitLateness(id: string, request: any, token?: string): Observable<void> {
    const params: { token?: string } = {};

    if (token) {
      params["token"] = token;
    }

    return this.http.put<void>(
      `${environment.snoots.api.calendar}/public/appointments/${id}/lateness`,
      request,
      { params }
    );
  }

  async print(appointments: Appointment[]): Promise<void> {
    await this.printService.printPdf(this.toPdf(appointments));
  }

  @UserNotify({
    successMessage: marker("Appuntamenti esportati in Excel"),
    errorMessage: marker("Impossibile esportare gli appuntamenti"),
    progressIndeterminate: true,
    progressMessage: marker("Esportazione appuntamenti in corso")
  })
  exportMassiveExcell(payload: {
    startAt: string;
    endAt: string;
    staffId?: string[] | undefined;
    roomId?: string[] | undefined;
  }): Observable<void> {
    return this.http.post<void>(
      `${environment.snoots.api.calendar}/appointments/exports`,
      pickBy({
        startAt: payload.startAt,
        endAt: payload.endAt,
        staffId: payload.staffId,
        roomId: payload.roomId
      })
    );
  }

  async toPdf(appointments: Appointment[]): Promise<Blob> {
    const currentLocale = this.localStorageService.getItem("locale") ?? "it";
    const localeMap: Record<string, string> = {
      it: "it-IT",
      en: "en-US"
    };

    const [appointmentTemplate, tailwindCss] = await Promise.all([
      fetch("/assets/ejs-templates/appointment/appointment.ejs").then(
        (response) => response.text()
      ),
      fetch("/assets/ejs-templates/appointment/tailwind.css").then((response) =>
        response.text()
      )
    ]);
    const htmlRendered = this.templateService.render(appointmentTemplate, {
      labels: {
        dateLabel: sntTranslate(marker("Data")),
        timeLabel: sntTranslate(marker("Ora")),
        patientLabel: sntTranslate(marker("Paziente")),
        customerLabel: sntTranslate(marker("Cliente")),
        staffLabel: sntTranslate(marker("Staff")),
        roomLabel: sntTranslate(marker("Stanza")),
        reasonLabel: sntTranslate(marker("Motivo della presentazione"))
      },
      appointmentList: appointments
        .sort(
          (a, b) =>
            new Date(a.startAt).getTime() - new Date(b.startAt).getTime()
        )
        .map(({ startAt, endAt, staff, room, customer, patient, summary }) => ({
          eventDate: dateUtil(startAt).format("DD/MM/YYYY"),
          eventTimeRange: `${dateUtil(startAt).format("HH:mm")} - ${dateUtil(endAt).format("HH:mm")}`,
          staffList: staff.map(
            ({ lastName, firstName }) => `${lastName} ${firstName}`
          ),
          room: !!room ? room.name : "",
          customerNominative: customer
            ? `${getCustomerNominative(customer, true)} (${customer?.number ?? "N/D"})`
            : "",
          patientName:
            patient && patient.name
              ? `${patient.name} (${patient?.number ?? "N/D"}${patient.species ? ` - ${sntTranslate(patient.species).toLowerCase()}` : ""})`
              : "",
          reason: summary
        }))
        .reduce(
          (acc, curr) => {
            const index = acc.findIndex(
              (event) => event.eventDate === curr.eventDate
            );

            if (index !== -1) {
              acc[index].appointments.push(curr);
            } else {
              acc.push({
                eventDate: curr.eventDate,
                appointments: [curr]
              });
            }

            return acc;
          },
          <
            Array<{
              eventDate: string;
              appointments: Array<{
                eventTimeRange: string;
                staffList: string[];
                room: string;
                customerNominative: string;
                patientName: string;
                reason: string;
              }>;
            }>
          >[]
        )
        .map((item) => {
          let eventDate = item.eventDate;

          const [day, month, year] = eventDate.split("/");
          const date = new Date(`${year}-${month}-${day}`);

          const formatted = new Intl.DateTimeFormat(localeMap[currentLocale], {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric"
          }).format(date);

          item.eventDate =
            formatted.charAt(0).toUpperCase() + formatted.slice(1);

          return item;
        }),
      tailwindCss
    });

    return await this.printService.htmlToPdfNoHeaderFooterAndMargins(
      htmlRendered,
      {
        landscape: true
      }
    );
  }
}

function extendAppointment(appointment: Appointment): Appointment {
  const durationInMinutes = dateUtil(appointment.endAt).diff(
    dateUtil(appointment.startAt),
    "minutes"
  );

  return {
    ...appointment,

    durationInMinutes,
    rows: Math.round(durationInMinutes / 15),
    itIsGone: dateUtil().isAfter(dateUtil(appointment.endAt))
  };
}
